// noinspection ES6PreferShortImport

import { BinaryUtils, GeometryUtils, ObjectUtils } from "@cic/utils";
import {
    CatalogFactory,
    CatalogVersionFactory,
    ClientItemFactory,
    CompleteCatalogVersionFactory,
    ConfigurationChangeFactory,
    IContentAPICatalog,
    IContentAPICatalogVersion,
    IContentAPIClientItem,
    IContentAPICompleteCatalogVersion,
    IContentAPIConfigurationChange,
    IContentAPIConfigurationState,
    IContentAPIItemFeatureOptions,
    IContentAPIItemFeatures,
    IContentAPIItemPricingReport,
    IContentAPIItemUpdateReport,
    IContentAPIItemVariant,
    IContentAPIItemVariantProposal,
    IContentAPISearchItemsResult,
    ItemFeatureOptionsFactory,
    ItemFeaturesFactory,
    ItemPricingReportFactory,
    ItemUpdateReportFactory,
    ItemVariantFactory,
    ItemVariantProposalFactory,
    SearchItemsResultFactory,
    IContentAPIFeatureState,
    IContentAPICommercialItemList,
    CommercialItemListFactory,
    IContentAPIPartnership,
    PartnershipFactory
} from "./index";

export interface IDSLibManifest {
    cjs: string,
    esm: string,
    libVersion: string,
    umd: string,
    wasmJs: string,
    wasmVersion: string
}

export interface IExtendedOptionsGetItemAutomations extends IOptionsGetItemAutomations {
    designServicesBaseUrl?: string; // base URL required to load design services lib
    requireJSImportBaseUrl?: string; // base URL to load require.js, which is neede for the design services lib.
    newLib?: boolean;  // newLib is to invoke the new WASM module : default value is false. If passing newLib as true, set sectionMode to false
    sectionMode?: boolean; // sectionMode is to get the output in section mode : default value is false
    mergeWithSameMaterial?: boolean; // mergeWithSameMaterial is to merge the items with same material : default value is false
    prepareForEdit?: boolean; // prepareForEdit is to prepare the design for edit (worktop/kickboard edit) : default value is false
    overhangFront?: number; // OverhangFront is to set the overhang for front side
    overhangBack?: number; // OverhangBack is to set the overhang for back side
    overhangSideEnd?: number; // OverhangSideEnd is to set the overhang for side end
    overhangSideBreak?: number; // OverhangSideBreak is to set the overhang for side break
    maxDistFromWall?: number; // maxDistFromWall is to set the maximum distance from wall
    returnUnaffectedItems?: boolean;
    useAutoLinearItems?: boolean;
    useGeometriesAsBuffers?: boolean;
}

interface ILinearItemProposals {
    [itemId: string]: Array<V2.IItemVariantProposal>;
}

export interface IDSLibModule {
    // baseUrl: "https://design-services.cyncly-idealspaces.com/cyndsrppdun01"
    manifest: IDSLibManifest,
    init: (a?: string, b?: string) => Promise<void>,
    getItemAutomations(designData: ICommonDesignFormat, options: IExtendedOptionsGetItemAutomations): Promise<IAPIResponse<Array<IClientItem>>>,
    getItemAutomationsCDF(designData: ICommonDesignFormat, options: IExtendedOptionsGetItemAutomations, linearItemProposals?: ILinearItemProposals, apiHandle?: ICiCAPI): Promise<ICommonDesignFormat>,
    setDebugLevel(level: number): Promise<void>,
    getVersion: () => Promise<string>
}

const AsyncFunction = Object.getPrototypeOf(async function () { }).constructor;

let DSLibModule: IDSLibModule | undefined;

const APISearchCaches: Map<string, IAPIResponseWithDetails<V2.ISearchItemsResult, ICatalogVersionErrorDetails>> = new Map();

class ContentAPISdkUtils {

    static async getSearchResults(searchText: string, apiHandle: ICiCAPI = CiCAPI, options: IOptionsSearchItems | V2.IOptionsSearchItems): Promise<IAPIResponseWithDetails<IContentAPISearchItemsResult, ICatalogVersionErrorDetails>> {
        let result: IContentAPISearchItemsResult | undefined,
            response: IAPIResponseWithDetails<V2.ISearchItemsResult, ICatalogVersionErrorDetails> = await apiHandle.content.v2.searchItems(searchText, options);

        if (response.result) {
            result = SearchItemsResultFactory.create(response.result);
        } else {
            console.warn("searchItems didn't return results", response.success, response.result, response.details);
            result = SearchItemsResultFactory.create({ total: 0, offset: options.offset ?? 0, nbPerPage: options.nbPerPage ?? 0, items: [] });
        }

        return Object.assign({}, response, { result }) as IAPIResponseWithDetails<IContentAPISearchItemsResult, ICatalogVersionErrorDetails>;
    }

    static async searchItems(searchText: string, apiHandle: ICiCAPI = CiCAPI, options: IOptionsSearchItems | V2.IOptionsSearchItems): Promise<IContentAPISearchItemsResult> {
        let searchItems: IContentAPISearchItemsResult | undefined;

        try {
            let cacheKey: string = `${searchText}:${JSON.stringify(options)}`;

            let response: IAPIResponseWithDetails<V2.ISearchItemsResult, ICatalogVersionErrorDetails>;

            if (APISearchCaches.has(cacheKey)) {
                response = APISearchCaches.get(cacheKey) as IAPIResponseWithDetails<V2.ISearchItemsResult, ICatalogVersionErrorDetails>;
            } else {
                response = await apiHandle.content.v2.searchItems(searchText, options);
                APISearchCaches.set(cacheKey, response);
            }

            if (response.success && response.result) {
                searchItems = SearchItemsResultFactory.create(response.result);
            } else {
                console.warn("searchItems didn't return results", response.success, response.result, response.details);
            }
        } catch (error) {
            console.error("searchItems::Failed", error);
        }

        if (!searchItems) {
            searchItems = SearchItemsResultFactory.create({ total: 0, offset: options.offset ?? 0, nbPerPage: options.nbPerPage ?? 0, items: [] });
        }

        return searchItems;
    }

    static async getItemVariant(clientItem: IContentAPIClientItem | IClientItemRef, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetItemVariant | V2.IOptionsGetItemVariant): Promise<IContentAPIItemVariant | undefined> {

        if (!clientItem.itemId) {
            throw new Error("Cannot fetch clientItem without an ID");
        }

        let itemVariant: IContentAPIItemVariant | undefined;
        try {
            let response = await apiHandle.content.v2.getItemVariant(clientItem, options);

            if (response.success && response.result) {
                itemVariant = ItemVariantFactory.create(response.result);
                this._resetClientItemPropertiesInItemVariant(itemVariant, clientItem);
            } else {
                console.warn("getItemVariant didn't return results", response.success, response.result, response.details);
            }
        } catch (error) {
            console.error("getItemVariant::Failed", error);
        }

        return itemVariant;
    }

    static async getItemVariantById(itemId: string, configurationState?: IContentAPIConfigurationState | IConfigurationState, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetItemVariant | V2.IOptionsGetItemVariant)
        : Promise<IAPIResponseWithDetails<IContentAPIItemVariant, Array<IContentAPIConfigurationChange>>> {

        let clientItem: IClientItemRef = { itemId, configurationState },
            response: IAPIResponseWithDetails<V2.IItemVariant, Array<IConfigurationChange>> = await apiHandle.content.v2.getItemVariant(clientItem, options),
            result: IContentAPIItemVariant | undefined,
            details: Array<IContentAPIConfigurationChange> | undefined;

        if (response.success && response.result) {
            result = ItemVariantFactory.create(response.result);
            if (response.details) {
                details = response.details.map((change: IConfigurationChange) => ConfigurationChangeFactory.create(change));
            }
        } else {
            console.warn("getItemVariant didn't return results", response.success, response.result, response.details);
        }

        return Object.assign({}, response, { result, details }) as IAPIResponseWithDetails<IContentAPIItemVariant, Array<IContentAPIConfigurationChange>>;
    }

    static async getItemVariants(clientItems: Array<IContentAPIClientItem | IClientItemRef>, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetItemVariant | V2.IOptionsGetItemVariant)
        : Promise<Array<IContentAPIItemVariant>> {

        let uniqueVariants: Map<string, IContentAPIItemVariant> = new Map(),
            promises: Array<Promise<unknown>>,
            itemVariants: Array<IContentAPIItemVariant>;

        promises = clientItems.map(async (clientItem: IContentAPIClientItem | IClientItemRef) => {
            if (clientItem.itemId) {
                let key: string = this._getItemVariantKey(clientItem),
                    response: IContentAPIItemVariant | undefined;

                if (!uniqueVariants.has(key)) {
                    response = await this.getItemVariant(clientItem, apiHandle, options);
                    if (response) {
                        uniqueVariants.set(key, response);
                    }
                }
            }
        });

        await Promise.all(promises);

        itemVariants = clientItems.map((clientItem: IContentAPIClientItem) => {
            let key: string = this._getItemVariantKey(clientItem),
                fetchedVariant: IContentAPIItemVariant | undefined;

            if (uniqueVariants.get(key)) {
                fetchedVariant = Object.assign({}, uniqueVariants.get(key));
                this._resetClientItemPropertiesInItemVariant(fetchedVariant, clientItem);
            }

            return fetchedVariant;
        }).filter((itemVariant: IContentAPIItemVariant | undefined) => itemVariant) as Array<IContentAPIItemVariant>;

        return itemVariants;
    }

    static async getItemVariantsFromCDF(designData: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options?: V2.IOptionsGetItemVariantsFromCDF, progressCallback?: IProgressCallback)
        : Promise<Array<IContentAPIItemVariant>> {

        let itemVariants: Array<IContentAPIItemVariant> = [];

        try {
            let response: IAPIResponseWithDetails<Array<V2.IItemVariant>, Array<string>> = await apiHandle.content.v2.getItemVariantsFromCDF(designData, options, progressCallback);
            if (response.details?.length) {
                console.error("getItemVariantsFromCDF failed for these item(s): ", response.details);
            }

            if (response.success && response.result) {
                itemVariants = response.result.map((itemVariantRef: V2.IItemVariant) => ItemVariantFactory.create(itemVariantRef));
            } else {
                console.warn("getItemVariantsFromCDF didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getItemVariantsFromCDF::Failed", e);
            itemVariants = [];
        }

        return itemVariants;
    }

    static async getItemAutomations(designData: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options?: IExtendedOptionsGetItemAutomations): Promise<Array<IContentAPIClientItem>> {
        let clientItems: Array<IContentAPIClientItem> = [];
        let updatedDesignData: ICommonDesignFormat = await this.getItemAutomationsCDF(designData, apiHandle, options);
        if (updatedDesignData?.items) {
            clientItems = updatedDesignData.items.map((item: ICommonDesignItem) => ClientItemFactory.create(item));
        }
        return clientItems;
    }

    static async getItemAutomationsCDF(designData: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options?: IExtendedOptionsGetItemAutomations): Promise<ICommonDesignFormat> {
        let updatedCDF: ICommonDesignFormat = designData;
        try {
            let hasConnectedItems: boolean = updatedCDF.items?.some((item: ICommonDesignItem) => item.connections && item.connections.length > 0) ?? false;
            if (!hasConnectedItems) return updatedCDF; // if no connected items, return original design data as there's no need to call the design services


            await this.loadDSLibModule(options?.designServicesBaseUrl, options?.requireJSImportBaseUrl);
            if (!DSLibModule) throw new Error("DSLibModule not loaded");
            // await DSLibModule.setDebugLevel(7); // for debugging


            // the DSLibModule seems to alter the input argument for designData... so we need to clone it in order
            // to prevent object pollution
            let designDataCopy: ICommonDesignFormat = ObjectUtils.cloneObj(designData);
            let designItemMap: Map<string, ICommonDesignItem> = new Map();
            designDataCopy.items?.forEach((item: ICommonDesignItem) => {
                designItemMap.set(item.id, item);
            });

            let dsLibOptions: IExtendedOptionsGetItemAutomations = Object.assign({}, options);
            delete dsLibOptions.designServicesBaseUrl;
            delete dsLibOptions.requireJSImportBaseUrl;
            if (dsLibOptions.includeGeometries) {
                dsLibOptions.sectionMode = false; // make sure the geometries generated are being merged
            } else {
                dsLibOptions.includeGeometries = false; // needs to be explicitly set, in case it's undefined
            }

            let result: ICommonDesignFormat;
            try {
                result = await DSLibModule.getItemAutomationsCDF(designDataCopy, dsLibOptions, undefined, apiHandle);
            } catch (e) {
                console.error("Error while calling getItemAutomationsCDF: ", e);
                return designData;
            }

            if (result?.items && result.items.length > 0) {
                let templateItems: Map<string, ICommonDesignItem> = new Map();
                designData.items?.forEach((item: ICommonDesignItem) => {
                    if (item.connections && item.connections.length > 0) {
                        templateItems.set(item.id, item);
                    }
                });

                // only filter if not returning unaffected items
                let itemsToUpdate: Array<ICommonDesignItem> = [];

                if (options?.returnUnaffectedItems && !options?.includeGeometries) {
                    if (result.items) {
                        result.items = result
                            .items
                            .filter((item: ICommonDesignItem) => {
                                return !templateItems.has(item.id);
                            })
                            .map((item: ICommonDesignItem) => {
                                delete item.connections;
                                return item;
                            });
                    }
                    templateItems.clear(); // no remapping needed then return unaffected items are true
                }

                itemsToUpdate = result.items;

                let sanitizedItems: Map<string, ICommonDesignItem> = new Map();
                for (let designItem of itemsToUpdate) {
                    let existingItem: ICommonDesignItem | undefined = templateItems.get(designItem.id);
                    if (designItem.connections && existingItem) {
                        await this._remapGeneratedItems(existingItem, designItem, apiHandle, options?.useGeometriesAsBuffers);
                        sanitizedItems.set(existingItem.id, existingItem);
                    } else {
                        sanitizedItems.set(designItem.id, designItem);
                    }
                }

                if (sanitizedItems.size > 0) {
                    updatedCDF = Object.assign({}, result, { items: Array.from(sanitizedItems.values()) });
                } else {
                    updatedCDF = designData;
                }

            } else {
                console.warn("getItemAutomationsV2 didn't return results", result);
            }
        } catch (e) {
            console.error("getItemAutomationsV2::Failed", e);
        }

        return updatedCDF;
    }

    static async getCompleteCatalogVersions(apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetCatalogs): Promise<Array<IContentAPICompleteCatalogVersion>> {
        let catalogs: Array<IContentAPICompleteCatalogVersion> = [];

        try {
            options = Object.assign({}, options, { visibleOnly: false });
            let response: IAPIResponse<Array<V2.ICatalog>> = await apiHandle.content.v2.getCatalogs(options);

            if (response.success && response.result) {
                response.result
                    .forEach((catalogRef: V2.ICatalog) => {
                        let catalog: IContentAPICatalog = CatalogFactory.create(catalogRef);

                        catalogRef.catalogVersions.map((catalogVersionRef: V2.ICatalogVersion) => {
                            let catalogVersion: IContentAPICatalogVersion = CatalogVersionFactory.create(catalogVersionRef);
                            let completeCatalog: IContentAPICompleteCatalogVersion = CompleteCatalogVersionFactory.create(catalog, catalogVersion);

                            catalogs.push(completeCatalog);
                        });
                    });
            } else {
                console.warn("getCompleteCatalogs didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getCompleteCatalogs::Failed", e);
            catalogs = [];
        }

        return catalogs;
    }

    static async getCatalogs(apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetCatalogs): Promise<Array<IContentAPICatalog>> {
        let catalogs: Array<IContentAPICatalog> = [],
            response: IAPIResponse<Array<V2.ICatalog>> = await apiHandle.content.v2.getCatalogs(options);

        if (response.success) {
            response.result?.forEach((catalogRef: V2.ICatalog) => {
                catalogs.push(CatalogFactory.create(catalogRef, catalogRef.catalogVersions));
            });
        } else {
            throw response.code;
        }

        return catalogs;
    }

    static async getCatalog(catalogId: string, apiHandle: ICiCAPI = CiCAPI): Promise<IContentAPICatalog | undefined> {
        let catalog: IContentAPICatalog | undefined;

        try {
            let response: IAPIResponse<V2.ICatalog> = await apiHandle.content.v2.getCatalog(catalogId);
            if (response.success && response.result) {
                catalog = CatalogFactory.create(response.result, response.result.catalogVersions);
            } else {
                console.warn("getCatalog didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getCatalog::Failed", e);
            catalog = undefined;
        }

        return catalog;
    }

    static async getPartnerships(apiHandle: ICiCAPI = CiCAPI): Promise<Array<IContentAPIPartnership>> {
        let partnerships: Array<IContentAPIPartnership> = [],
            response: IAPIResponse<Array<IPartnership>> = await apiHandle.content.v2.getPartnerships();

        if (response.success) {
            response.result?.forEach((partnership: IPartnership) => {
                partnerships.push(PartnershipFactory.create(partnership));
            });
        } else {
            throw response.code;
        }

        return partnerships;
    }

    static async downloadCatalogVersion(catalogVersionId: string, apiHandle: ICiCAPI = CiCAPI): Promise<IAPIResponse<OfflineResponseCode>> {
        return apiHandle.content.v2.downloadCatalogVersion(catalogVersionId);
    }

    static async removeDownloadedCatalogVersion(catalogVersionId: string, apiHandle: ICiCAPI = CiCAPI): Promise<IAPIResponse<OfflineResponseCode>> {
        return apiHandle.content.v2.removeDownloadedCatalogVersion(catalogVersionId);
    }

    static async requestCatalogAccess(catalogId: string, apiHandle: ICiCAPI = CiCAPI): Promise<IAPIResponse<RequestAccessStatus>> {
        return apiHandle.content.v2.requestCatalogAccess(catalogId);
    }

    static async getCatalogVersionItemGroups(catalogVersionId: string, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetCatalogVersionItemGroups): Promise<Array<IItemGroup>> {
        let groups: Array<IItemGroup> = [];
        try {
            let response: IAPIResponse<Array<IItemGroup>> = await apiHandle.content.v2.getCatalogVersionItemGroups(catalogVersionId, options);
            if (response.success && Array.isArray(response.result)) {
                groups = response.result;
            } else {
                console.warn("getCatalogVersionItemGroups didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getCatalogVersionItemGroups::Failed", e);
            groups = [];
        }

        return groups ?? [];
    }

    static async getItemCountsForSearchQueries(searchQueries: Record<string, IOptionsSearchItems>, apiHandle: ICiCAPI = CiCAPI): Promise<Record<string, number | string>> {
        let result: Record<string, number | string> = {};
        try {
            let response: IAPIResponse<Record<string, number | string>> = await apiHandle.content.v2.getItemCountsForSearchQueries(searchQueries);
            if (response.success && response.result) {
                result = response.result;
            } else {
                console.warn("getItemCountsForSearchQueries didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getItemCountsForSearchQueries::Failed", e);
            result = {};
        }

        return result;
    }

    static async getItemVariantProposals(clientItem: IClientItemRef, apiHandle: ICiCAPI = CiCAPI, options: IOptionsGetItemVariantProposals): Promise<Array<IContentAPIItemVariantProposal>> {
        let variantProposals: Array<IContentAPIItemVariantProposal> = [];
        try {
            let { itemId, configurationState }: IClientItemRef = clientItem;
            if (itemId) {
                const response: IAPIResponse<Array<V2.IItemVariantProposal>> = await apiHandle.content.v2.getItemVariantProposals({ itemId, configurationState }, options);
                if (response.success && Array.isArray(response.result)) {
                    variantProposals = response.result.map((itemVariantProposal: V2.IItemVariantProposal) => ItemVariantProposalFactory.create(itemVariantProposal));
                } else {
                    console.warn("getItemVariantProposals didn't return results", response.success, response.result);
                }
            } else {
                console.warn("getItemVariantProposals didn't receive a proper itemId");
            }
        } catch (e) {
            console.error("getItemVariantProposals::Failed", e);
            variantProposals = [];
        }

        return variantProposals;
    }

    static async getItemDynamicFeatures(item: IClientItemRef, apiHandle: ICiCAPI = CiCAPI, options?: IGetItemDynamicFeaturesOptions): Promise<Array<IDynamicFeature>> {
        let dynamicFeatures: Array<IDynamicFeature> = [];
        try {
            const response: IAPIResponse<Array<IDynamicFeature>> = await apiHandle.content.v2.getItemDynamicFeatures(item, options);
            if (response.success && Array.isArray(response.result)) {
                dynamicFeatures = response.result;
            } else {
                console.warn("getItemDynamicFeatures didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getItemDynamicFeatures::Failed", e);
            dynamicFeatures = [];
        }

        return dynamicFeatures;
    }

    static async getItemFeatures(items: Array<IClientItemRef>, apiHandle: ICiCAPI = CiCAPI, options?: V2.IOptionsGetItemFeatures): Promise<IContentAPIItemFeatures | undefined> {
        let features: IContentAPIItemFeatures | undefined;

        try {
            const response: IAPIResponse<V2.IItemFeatures> = await apiHandle.content.v2.getItemFeatures(items, options);
            if (response.success && response.result) {
                features = ItemFeaturesFactory.create(response.result);
            } else {
                console.warn("getItemFeatures didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getItemFeatures::Failed", e);
            features = undefined;
        }

        return features;
    }

    static async getItemFeatureOptions(items: Array<IClientItemRef>, featureId: string, apiHandle: ICiCAPI = CiCAPI, options?: V2.IOptionsGetItemFeatureOptions): Promise<IContentAPIItemFeatureOptions | undefined> {

        let featureOptions: IContentAPIItemFeatureOptions | undefined;

        try {
            const response: IAPIResponse<V2.IItemFeatureOptions> = await apiHandle.content.v2.getItemFeatureOptions(items, featureId, options);
            if (response.success && response.result) {
                featureOptions = ItemFeatureOptionsFactory.create(response.result);
            } else {
                console.warn("getItemFeatureOptions didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("getItemFeatureOptions::Failed", e);
            featureOptions = undefined;
        }

        return featureOptions;
    }

    static async getItemGeometry(clientItem: IClientItemRef, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetItemGeometry | V2.IOptionsGetItemGeometry): Promise<GLB | undefined> {

        let geometry: GLB | undefined;
        try {
            if (clientItem.itemId) {
                let response: IAPIResponse<GLB> = await apiHandle.content.v2.getItemGeometry(clientItem, options);
                if (response.success && response.result) {
                    geometry = response.result;
                } else {
                    console.warn("getItemGeometry didn't return results", response.success, response.result);
                }
            } else {
                console.warn("getItemGeometry didn't receive a proper itemId");
            }
        } catch (e) {
            console.error("getItemGeometry::Failed", e);
            geometry = undefined;
        }

        return geometry;
    }

    static async getItemUpdateReport(commonDesignFormat: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetItemUpdateReport): Promise<IContentAPIItemUpdateReport | undefined> {

        let response: IAPIResponse<IItemUpdateReport> = await apiHandle.content.v2.getItemUpdateReport(commonDesignFormat, options),
            result: IContentAPIItemUpdateReport | undefined;

        if (response.success && response.result) {
            result = ItemUpdateReportFactory.create(response.result);
        } else {
            console.warn("getItemUpdateReport didn't return results", response.success, response.result);
        }

        return result;
    }

    static async getItemPricingReport(commonDesignFormat: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options: IOptionsGetItemPricingReport): Promise<IAPIResponseWithDetails<IContentAPIItemPricingReport, IContentAPIItemPricingReport>> {

        let cdf: ICommonDesignFormat = commonDesignFormat;

        if (apiHandle.getConfig("contentPlatform.useDesignServices")) {
            let automationOptions: IExtendedOptionsGetItemAutomations = Object.assign({}, options, {
                sectionMode: true,
                newLib: true,
                includeGeometries: false,
                returnUnaffectedItems: true
            });
            cdf = await this.getItemAutomationsCDF(commonDesignFormat, apiHandle, automationOptions);
        }

        let response: IAPIResponseWithDetails<IItemPricingReport, IItemPricingReport> = await apiHandle.content.v2.getItemPricingReport(cdf, options),
            result: IContentAPIItemPricingReport | undefined,
            details: IContentAPIItemPricingReport | undefined;

        if (response.success && response.result) {
            result = ItemPricingReportFactory.create(response.result);
        } else {
            console.warn("getItemPricingReport returned", response.success, response.details);
            if (response.details) {
                details = ItemPricingReportFactory.create(response.details);
            }
        }

        return Object.assign({}, response, { result, details }) as IAPIResponseWithDetails<IContentAPIItemPricingReport, IContentAPIItemPricingReport>;
    }

    static async getItemPricingReportV1(items: Array<IClientItemRef>, apiHandle: ICiCAPI = CiCAPI, options: IOptionsGetItemPricingReport): Promise<IAPIResponseWithDetails<IContentAPIItemPricingReport, IContentAPIItemPricingReport>> {

        let response: IAPIResponseWithDetails<IItemPricingReport, IItemPricingReport> = await apiHandle.content.getItemPricingReport(items, options),
            result: IContentAPIItemPricingReport | undefined,
            details: IContentAPIItemPricingReport | undefined;

        if (response.success && response.result) {
            result = ItemPricingReportFactory.create(response.result);
        } else {
            console.warn("getItemPricingReport didn't return results", response.success, response.result, response.details);
            if (response.details) {
                details = ItemPricingReportFactory.create(response.details);
            }
        }

        return Object.assign({}, response, { result, details }) as IAPIResponseWithDetails<IContentAPIItemPricingReport, IContentAPIItemPricingReport>;
    }

    static async getCommercialItemList(commonDesignFormat: ICommonDesignFormat, apiHandle: ICiCAPI = CiCAPI, options?: IOptionsGetCommercialItemList): Promise<IAPIResponseWithDetails<IContentAPICommercialItemList, IContentAPICommercialItemList>> {

        let response: IAPIResponseWithDetails<ICommercialItemList, ICommercialItemList> = await apiHandle.content.v2.getCommercialItemList(commonDesignFormat, options),
            result: IContentAPICommercialItemList | undefined,
            details: IContentAPICommercialItemList | undefined;

        if (response.success && response.result) {
            result = CommercialItemListFactory.create(response.result);
        } else {
            console.warn("getCommercialItemList returned results with possible errors : ", response.success, response.result);
            if (response.details) {
                details = CommercialItemListFactory.create(response.details);
            }
        }

        return Object.assign({}, response, { result, details }) as IAPIResponseWithDetails<IContentAPICommercialItemList, IContentAPICommercialItemList>;
    }

    static async disassembleItem(clientItem: IClientItem, apiHandle: ICiCAPI = CiCAPI): Promise<Array<IContentAPIClientItem>> {
        let disassembledItems: Array<IContentAPIClientItem> = [];
        try {
            const response: IAPIResponse<Array<IClientItem>> = await apiHandle.content.v2.disassembleItem(clientItem);
            if (response.success && Array.isArray(response.result)) {
                disassembledItems = response.result.map((item: IClientItem) => ClientItemFactory.create(item));
            } else {
                console.warn("disassembleItem didn't return results", response.success, response.result);
            }
        } catch (e) {
            console.error("disassembleItem::Failed", e);
            disassembledItems = [];
        }

        return disassembledItems;
    }

    static configurationStatesToCacheKey(configurationState?: IConfigurationState, filterBySource: boolean = true, stripDimensionFOStates: boolean = false): string {
        let cacheKey: string = "[",
            featureStates: Array<IFeatureState> = Array.from(configurationState ?? []); // de-ref original list

        //!!!!
        //NOTE: Any changes here should also be reflected in the ItemManager.configurationStatesToCacheKey method
        //!!!!

        if (featureStates && featureStates.length > 0) {
            Array.from(featureStates)
                .sort(this._sortFeatureStates)
                .forEach((featureState: IFeatureState) => {
                    if (stripDimensionFOStates &&
                        featureState.source !== "automation" &&
                        featureState.featureClassification?.baseFeatureType === "dimension" &&
                        featureState.featureClassification.characteristics &&
                        featureState.featureClassification.characteristics.dimension &&
                        featureState.featureId?.endsWith("_" + featureState.featureClassification.characteristics.dimension as string)
                    ) return;

                    if (!filterBySource || featureState.source !== "default") {
                        let featureScope: string = featureState.subItemScope ? featureState.subItemScope + "/" : "",
                            featureStateValue: string = featureState.value === undefined ? "" : "=" + featureState.value;

                        cacheKey += featureScope + featureState.featureId + "=" + featureState.optionId + featureStateValue + "|" + featureState.source + ";";
                    }
                });
        }

        return cacheKey += "]";
    }

    static findContainerDivByClassName(node: Element | null, className: string): Element | null {
        if (!node) return null;

        let foundNode: Element | null = null;

        if ((node as HTMLElement).classList.contains(className)) {
            foundNode = node;
        }

        if (!foundNode) {
            foundNode = ContentAPISdkUtils.findContainerDivByClassName(node.parentElement, "doc-selector-entry");
        }

        return foundNode;
    }

    /* to make sure we don't call the refresh too often */
    static debounce(funcToDebounce: Function, debounceTime: number): Function {
        let timeoutHandle: number;
        return function () {
            clearTimeout(timeoutHandle);
            timeoutHandle = setTimeout(() => funcToDebounce.apply(null, Array.from(arguments)), debounceTime) as any;
        };
    }

    static async loadDSLibModule(dsLibBaseUrl?: string, baseUrl?: string): Promise<IDSLibModule | undefined> {
        if (DSLibModule) return DSLibModule;

        if (!dsLibBaseUrl) {
            console.warn("Can't load design services lib without base URL.");
            return;
        }

        if (!dsLibBaseUrl?.endsWith("/")) {
            dsLibBaseUrl += "/";
        }

        // @ts-ignore
        if (typeof process === "object") {
            DSLibModule = await this.loadDSLibNodeModule(dsLibBaseUrl);
        } else {
            DSLibModule = await this.loadDSLibWebModule(dsLibBaseUrl, baseUrl);
        }

        return DSLibModule;
    }

    static async loadDSLibNodeModule(dsLibBaseUrl?: string): Promise<IDSLibModule | undefined> {
        if (!dsLibBaseUrl) {
            console.warn("Can't load design services lib without base URL.");
            return;
        }

        let filesToLoad: Array<string> = [
            "DesignServicesLoader.js",
            "DesignServicesModule.mjs",
            "DesignServices.js",
            "DesignServices.wasm"
        ];

        let dsLibModule: IDSLibModule | undefined;
        try {
            // @ts-ignore
            let localRequire = __non_webpack_require__ ?? require;
            let os = localRequire("os"),
                fs = localRequire("fs/promises"),
                path = localRequire("path"),
                { pathToFileURL } = localRequire("url"),
                { Buffer } = localRequire("buffer"),
                process = localRequire("process");

            let tempDir: string = await fs.mkdtemp(path.join(os.tmpdir(), "dsLib-"));
            if (process.platform === 'win32') {
                // Split by the Win32 separator and join with the POSIX separator
                tempDir = tempDir.split(path.win32.sep).join(path.posix.sep);
            }

            for (let fileToLoad of filesToLoad) {
                let response: Response = await fetch(dsLibBaseUrl + fileToLoad, { cache: "no-cache" });
                let buffer: ArrayBuffer = await response.arrayBuffer();
                let filePath: string = path.join(tempDir, fileToLoad);
                let binContent = Buffer.from(buffer);

                // Patch all downloaded JS files so that any internal dynamic import() calls
                // convert Windows absolute paths to file:// URLs. Without this, Electron's
                // renderer fails with ERR_UNSUPPORTED_ESM_URL_SCHEME on Windows paths like "C:\...".
                if (fileToLoad.endsWith("js")) {
                    let src: string = binContent.toString("utf8");
                    let patchedSrc: string =
                        `var __dynImport=function(p){return import(/^[a-zA-Z]:/.test(p)?require('url').pathToFileURL(p).href:p)};\n` +
                        src.replace(/\bimport\s*\(/g, "__dynImport(");
                    await fs.writeFile(filePath, patchedSrc, "utf8");
                } else {
                    await fs.writeFile(filePath, binContent);
                }
            }

            let dynImport = new AsyncFunction(`
                require = this.nativeRequire;
                let loaderModule = await import('${pathToFileURL(path.join(tempDir, filesToLoad[0])).href}');
                return loaderModule.createDesignServicesLoader('${tempDir}');
            `);

            dsLibModule = await dynImport.call({
                // @ts-ignore
                nativeRequire: localRequire // in case no run within webpack built app.
            });

            // cleanup
            await fs.rm(tempDir, { recursive: true });

        } catch (e) {
            console.error("Failed to load DSLibModule...", e);
        }
        return dsLibModule;
    }

    static async loadDSLibWebModule(dsLibBaseUrl?: string, baseUrl?: string): Promise<IDSLibModule | undefined> {
        if (!dsLibBaseUrl || !baseUrl) {
            console.warn("Can't load design services lib and requireJS base URL.");
            return;
        }

        let dsLibModule: IDSLibModule | undefined;
        try {
            await this._loadRequireJS(baseUrl);

            const dsLibLoaderUrl: string = `${dsLibBaseUrl}DesignServicesLoader.js`;
            const response = await fetch(dsLibLoaderUrl, { cache: "no-cache" });

            if (response.ok) {
                const loaderCode = await response.text();
                const blob = new Blob([loaderCode], { type: "application/javascript" });
                const blobUrl = URL.createObjectURL(blob);
                const { createDesignServicesLoader } = await import(/* webpackIgnore: true */ /* @vite-ignore */ blobUrl);
                dsLibModule = createDesignServicesLoader(dsLibBaseUrl);
                URL.revokeObjectURL(blobUrl);
            }
        } catch (e) {
            console.error("Failed to load DSLibModule...", e);
        }

        return dsLibModule;
    }

    private static _mergeCDFItems(srcCDF: ICommonDesignFormat, dstCDF: ICommonDesignFormat): ICommonDesignFormat {
        let cdfItemsMap: Map<string, ICommonDesignItem> = new Map();

        srcCDF.items?.forEach((item: ICommonDesignItem) => {
            cdfItemsMap.set(item.id, item);
        });

        dstCDF.items?.forEach((item: ICommonDesignItem) => {
            cdfItemsMap.set(item.id, item);
        });

        return Object.assign({}, srcCDF, { items: Array.from(cdfItemsMap.values()) });
    }

    private static async _remapGeneratedItems(srcDesignItem: ICommonDesignItem, dstDesignItem: ICommonDesignItem, apiHandle: ICiCAPI = CiCAPI, useGeometriesAsBuffers?: boolean): Promise<void> {
        if (dstDesignItem.geometries) {
            let srcGLBResponse: IAPIResponse<GLB> = await apiHandle.content.v2.getItemGeometry({
                itemId: srcDesignItem.catalogItemId ?? srcDesignItem.itemId,
                configurationState: srcDesignItem.configurationState
            }, { geometryType: "main" });

            if (!srcGLBResponse || !srcGLBResponse.result) return;

            // @ts-ignore -- global is referenced in node and referenced the browsers's globalThis object
            if (!globalThis.global) {
                // @ts-ignore
                globalThis.global = globalThis;
            }
            let updatedGLB: GLB = await GeometryUtils.transferMaterials(srcGLBResponse.result, dstDesignItem.geometries.main as string);
            if (!srcDesignItem.geometries) {
                srcDesignItem.geometries = {};
            }
            if (useGeometriesAsBuffers) {
                srcDesignItem.geometries.main = updatedGLB;
            } else {
                srcDesignItem.geometries.main = await BinaryUtils.convertBufferToHeadlessBase64(updatedGLB);
            }
        }

        // update existing item
        srcDesignItem.position = dstDesignItem.position;
        srcDesignItem.orientation = dstDesignItem.orientation;
        srcDesignItem.dimensions = dstDesignItem.dimensions;
    }

    private static _getItemVariantKey(clientItem: IContentAPIClientItem | IClientItemRef): string {
        return clientItem.itemId + this.configurationStatesToCacheKey(clientItem.configurationState);
    }

    private static _resetClientItemPropertiesInItemVariant(destinationItemVariant: IContentAPIItemVariant, sourceClientItem: IContentAPIClientItem): void {
        let extras: Record<string, unknown> | undefined = sourceClientItem.instanceExtras;

        if (extras && extras.selected) {
            destinationItemVariant.selected = extras.selected as boolean;
        }

        if (sourceClientItem.position) {
            destinationItemVariant.position = sourceClientItem.position as V2.Vector3;
        }

        if (sourceClientItem.orientation) {
            destinationItemVariant.orientation = sourceClientItem.orientation as V2.Vector3;
        }

        destinationItemVariant.uuid = sourceClientItem.instanceId;
    }

    private static _sortFeatureStates(stateB: IContentAPIFeatureState, stateA: IContentAPIFeatureState): number {
        let valueA: string = `${stateA.featureId}`,
            valueB: string = `${stateB.featureId}`,
            rc: number = 0;

        if (valueA < valueB) {
            rc = -1;
        } else if (valueA > valueB) {
            rc = 1;
        }
        return rc;
    }

    private static async _loadRequireJS(baseUrl: string): Promise<void> {
        // @ts-ignore
        if (window.require && window.define && window.requirejs) return;

        let resp: Response;
        const requireUrl: string = baseUrl + "/require.min.js";
        try {
            resp = await fetch(requireUrl, { cache: "no-cache" });
        } catch (e) {
            throw new Error(`Failed to fetch RequireJS from "${requireUrl}": ${String(e)}`);
        }
        if (!resp.ok) {
            throw new Error(`Failed to load RequireJS from "${requireUrl}": HTTP ${resp.status} ${resp.statusText || ""}`.trim());
        }
        const contentType: string | null = resp.headers ? resp.headers.get("content-type") : null;
        if (
            contentType &&
            !contentType.toLowerCase().includes("javascript") &&
            !contentType.toLowerCase().includes("ecmascript") &&
            !contentType.toLowerCase().includes("text/plain")
        ) {
            throw new Error(`Unexpected content type "${contentType}" when loading RequireJS from "${requireUrl}".`);
        }

        let code: string = await resp.text();

        let loader: Function = new Function(`
            ${code}

            this.requirejs = requirejs;
            this.require = require;
            this.define = define;

            return [requirejs,require,define];
        `);

        loader.call(globalThis);
    }
}

export default ContentAPISdkUtils;